import api from "./api";
import { ERROR_CODES } from "../config/setting";

const sanitizeResponseData = (checkData) => {
  const responseData = checkData?.data;
  if (responseData?.result?.error_no === ERROR_CODES.ExpiredADB2CToken) {
    // トークンの有効期限切れの場合ログイン画面に遷移
    window.location.href = "/account-login";
  }

  return responseData;
};

/**
 * 予約メニュー一覧取得API
 * @param {string} medicalInstitutionId 病院ID
 * @return {Object} 診療科情報
 */
export const getReservationMenus = async (medicalInstitutionId) => {
  const response = await api.get(`/getReservationMenus`, {
    params: { medicalInstitutionId },
  });

  return sanitizeResponseData(response);
};

/**
 * 医療機関情報取得API
 * @param {string} medicalInstitutionId 病院ID
 * @return {Object} 医療機関情報
 */
export const fetchClinic = async (medicalInstitutionId) => {
  const response = await api.get(`/clinic/${medicalInstitutionId}`);

  return sanitizeResponseData(response);
};

/**
 * 予約カレンダー情報取得API
 * @param {string} medicalInstitutionId 病院ID
 * @param {string} reservationMenuId メニューID
 * @param {string} date 日付 (YYYY-MM-DD形式)
 * @return {Object}  予約カレンダー情報
 */
export const getReservationCalendar = async (
  medicalInstitutionId,
  reservationMenuId,
  date
) => {
  const response = await api.get(`/getReservationCalendar`, {
    params: { medicalInstitutionId, reservationMenuId, date },
  });

  return sanitizeResponseData(response);
};

/**
 * 予約日時変更API
 * @param {string} reservationToken 予約トークン
 * @param {string} newDate 日
 * @param {string} newTime 時
 * @return {Object} ログイン結果
 */
export const changeReservationDateTime = async (
  reservationToken,
  newDate,
  newTime
) => {
  const response = await api.post("/changeReservationDateTime", {
    token: reservationToken,
    newDate,
    newTime,
  });

  return sanitizeResponseData(response);
};

/**
 * ログインAPI
 * @param {string} email メールアドレス
 * @param {string} password パスワード
 * @return {Object} ログイン結果
 */
export const patientWebLogin = async (email, password) => {
  const response = await api.post("/patientWebLogin", {
    email,
    password,
  });

  return sanitizeResponseData(response);
};

/**
 * ユーザ情報取得API
 * @param {string} access_token アクセストークン
 * @return {Object} ユーザ情報
 */
export const getUserInfo = async (access_token) => {
  const response = await api.get(`/getUserInfo`, {
    headers: {
      Authorization: `${access_token}`,
    },
  });

  return sanitizeResponseData(response);
};

/**
 * 予約登録API
 * @param {Object} reservationInfo 予約情報
 * @param {Object} patientInfo 患者情報
 * @return {Object} 結果
 */
export const registReservation = async (reservationInfo, patientInfo) => {
  const response = await api.post("/registReservation", {
    reservationInfo,
    patientInfo,
  });

  return sanitizeResponseData(response);
};

/**
 * 予約詳細取得API
 * @param {string} reservationToken 予約トークン
 * @return {Object} 予約情報
 */
export const getReservationDetail = async (reservationToken) => {
  const response = await api.get(`/getReservationDetail`, {
    params: { token: reservationToken },
  });

  return sanitizeResponseData(response);
};

/**
 * 予約キャンセルAPI
 * @param {string} reservationToken 予約トークン
 * @return {Object} 結果
 */
export const cancelReservation = async (reservationToken) => {
  const response = await api.post("/cancelReservation", {
    token: reservationToken,
  });

  return sanitizeResponseData(response);
};

/**
 * 問診票取得API
 * @param {string} questionnaireToken 問診トークン
 * @return {Object} 問診情報
 */
export const getMedicalQuestionnaire = async (questionnaireToken) => {
  const response = await api.get(`/getMedicalQuestionnaire`, {
    params: { token: questionnaireToken },
  });

  return sanitizeResponseData(response);
};

/**
 * 問診回答送信API
 * @param {string} medicalInstitutionId 病院ID
 * @param {string} questionnaireToken 問診トークン
 * @param {Object} data 回答情報
 * @return {Object} 結果
 */
export const registPatientMedicalQuestionnaire = async (
  medicalInstitutionId,
  questionnaireToken,
  data
) => {
  const response = await api.post("/registPatientMedicalQuestionnaire", {
    medicalInstitutionId,
    token: questionnaireToken,
    data,
  });

  return sanitizeResponseData(response);
};

/**
 * アカウント登録（メール送信）API
 * @param {string} email メールアドレス
 * @param {string} password パスワード
 * @param {string} kanjiLast 患者姓
 * @param {string} kanjiFirst 患者名
 * @param {string} reSendFlag 再送かどうか
 * @return {Object} 結果
 */
export const sendCertificationNumberMailWhenAccountRegist = async (
  email,
  kanjiLast,
  kanjiFirst,
  reSendFlag,
  password
) => {
  const response = await api.post(
    "/sendCertificationNumberMailWhenAccountRegist",
    {
      email,
      kanjiLast,
      kanjiFirst,
      reSendFlag,
      password,
    }
  );

  return sanitizeResponseData(response);
};

/**
 * アカウント登録（アカウント登録）API
 * @param {string} certificationNumber 認証番号(Base64)
 * @param {string} inputPatientInfo 患者情報
 * @return {Object} 結果
 */
export const registAccount = async (certificationNumber, inputPatientInfo) => {
  const response = await api.post("/registAccount", {
    certificationNumber,
    inputPatientInfo,
  });

  return sanitizeResponseData(response);
};

/**
 * パスワード再設定（メールアドレス入力）API（ログイン後）
 * @param {string} access_token アクセストークン
 * @param {string} email メールアドレス
 * @return {Object} 結果
 */
export const sendCertificationNumberMailWhenPasswordReset = async (
  access_token,
  email
) => {
  const response = await api.post(
    "/sendCertificationNumberMailWhenPasswordReset",
    {
      email,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};
/**
 * パスワード再設定（メールアドレス入力）API（ログイン前）
 * @param {string} email メールアドレス
 * @return {Object} 結果
 */
export const sendCertificationNumberMailWhenPasswordResetBeforeLogin = async (
  email
) => {
  const response = await api.post(
    "/sendCertificationNumberMailWhenPasswordReset",
    {
      email,
    }
  );

  return sanitizeResponseData(response);
};
/**
 * パスワード再設定（認証番号入力）API（ログイン後）
 * @param {string} access_token アクセストークン
 * @param {string} certificationNumber 認証番号(Base64)
 * @param {string} email メールアドレス
 * @return {Object} 結果
 */
export const checkCertificationNumber = async (
  access_token,
  certificationNumber,
  email
) => {
  const response = await api.post(
    "/checkCertificationNumber",
    {
      certificationNumber,
      email,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};
/**
 * パスワード再設定（認証番号入力）API（ログイン前）
 * @param {string} certificationNumber 認証番号(Base64)
 * @param {string} email メールアドレス
 * @return {Object} 結果
 */
export const checkCertificationNumberBeforeLogin = async (
  certificationNumber,
  email
) => {
  const response = await api.post("/checkCertificationNumber", {
    certificationNumber,
    email,
  });

  return sanitizeResponseData(response);
};
/**
 * パスワード再設定（パスワード入力）API
 * @param {string} access_token アクセストークン
 * @param {string} password パスワード(Base64)
 * @param {string} email メールアドレス
 * @return {Object} 結果
 */
export const resetPassword = async (access_token, password, email) => {
  const response = await api.post(
    "/resetPassword",
    {
      password,
      email,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};

/**
 * ログイン後パスワード再設定（パスワード入力）API
 * @param {string} access_token アクセストークン
 * @param {string} password パスワード(Base64)
 * @param {string} email メールアドレス
 * @return {Object} 結果
 */
export const resetPasswordAfterLogin = async (
  access_token,
  password,
  email,
  kanjiLast,
  kanjiFirst
) => {
  const response = await api.post(
    "/resetPasswordAfterLogin",
    {
      password,
      email,
      kanjiLast,
      kanjiFirst,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};

/**
 * メールアドレス変更（メール送信）API
 * @param {string} access_token アクセストークン
 * @param {string} email メールアドレス
 * @param {string} kanjiLast 姓
 * @param {string} kanjiFirst 名
 * @return {Object} 結果
 */
export const sendCertificationNumberMailWhenEmailUpdate = async (
  access_token,
  email,
  kanjiLast,
  kanjiFirst
) => {
  const response = await api.post(
    "/sendCertificationNumberMailWhenEmailUpdate",
    {
      email,
      kanjiLast,
      kanjiFirst,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};

/**
 * メールアドレス変更（メールアドレス変更）API
 * @param {string} access_token アクセストークン
 * @param {string} certificationNumber 認証番号(Base64)
 * @param {string} oldMailAddress 旧メールアドレス
 * @param {string} email メールアドレス
 * @param {string} kanjiLast 姓
 * @param {string} kanjiFirst 名
 * @return {Object} 結果
 */
export const updateEmail = async (
  access_token,
  certificationNumber,
  oldEmail,
  email,
  kanjiLast,
  kanjiFirst
) => {
  const response = await api.post(
    "/updateEmail",
    {
      certificationNumber,
      oldEmail,
      email,
      kanjiLast,
      kanjiFirst,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};

/**
 * 個人情報変更API
 * @param {string} access_token アクセストークン
 * @param {Object} patientInfo 患者情報
 * @return {Object} 結果
 */
export const updateUserInfo = async (access_token, patientInfo) => {
  const response = await api.post(
    "/updateUserInfo",
    {
      patientInfo,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};

/**
 * 家族情報取得API
 * @param {string} adb2cId ADB2CID
 * @param {string} access_token アクセストークン
 * @return {Object} 結果
 */
export const getFamilyInfo = async (adb2cId, access_token) => {
  const response = await api.get("/getFamilyInfo", {
    headers: {
      Authorization: `${access_token}`,
    },
    params: { adb2cId },
  });

  return sanitizeResponseData(response);
};

/**
 * 家族情報変更API
 * @param {string} access_token アクセストークン
 * @param {string} email メールアドレス
 * @param {Object} familyInfo 家族情報
 * @return {Object} 結果
 */
export const updateFamilyInfo = async (access_token, email, familyInfo) => {
  const response = await api.post(
    "/updateFamilyInfo",
    {
      email,
      familyInfo,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};

/**
 * 家族情報登録API
 * @param {string} access_token アクセストークン
 * @param {string} email メールアドレス
 * @param {Object} familyInfo 家族情報
 * @return {Object} 結果
 */
export const registFamilyInfo = async (access_token, email, familyInfo) => {
  const response = await api.post(
    "/registFamilyInfo",
    {
      email,
      familyInfo,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};

/**
 * 家族情報削除API
 * @param {string} access_token アクセストークン
 * @param {string} email メールアドレス
 * @param {string} familyMemberId 家族ID
 * @return {Object} 結果
 */
export const deleteFamilyInfo = async (access_token, email, familyMemberId) => {
  const response = await api.delete("/deleteFamilyInfo", {
    headers: {
      Authorization: `${access_token}`,
    },
    data: {
      email,
      familyMemberId,
    },
  });

  return sanitizeResponseData(response);
};

/**
 * 受診済医療機関取得API
 * @param {string} access_token アクセストークン
 * @param {string} adb2cId ADB2CID
 * @return {Object} 問診情報
 */
export const getMedicalInstituitionList = async (access_token, adb2cId) => {
  const response = await api.get("/getMedicalInstituitionList", {
    headers: {
      Authorization: `${access_token}`,
    },
    params: { adb2cId },
  });

  return sanitizeResponseData(response);
};

/**
 * 退会手続きAPI
 * @param {string} access_token アクセストークン
 * @param {string} email メールアドレス
 * @param {string} kanjiLast 姓
 * @param {string} kanjiFirst 名
 * @return {Object} 結果
 */
export const sendUnsubscribe = async (
  access_token,
  email,
  kanjiLast,
  kanjiFirst
) => {
  const response = await api.post(
    "/sendUnsubscribe",
    {
      email,
      kanjiLast,
      kanjiFirst,
    },
    {
      headers: {
        Authorization: `${access_token}`,
      },
    }
  );

  return sanitizeResponseData(response);
};
